<template>
  <div class="details-screen">
    <!-- Header -->
    <div class="screen-header">
      <button class="screen-header__back" @click="goBack">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 class="screen-header__title">{{ deckTitle || "Flash Cards" }}</h1>
    </div>

    <!-- Content -->
    <div class="details-content">
      <!-- Status Section -->
      <section class="details-section">
        <!-- Loading -->
        <div v-if="loading" class="status-card status-card--loading">
          <div class="spinner"></div>
          <span>{{ t("loading") }}</span>
        </div>

        <!-- Processing -->
        <div
          v-else-if="isProcessing"
          class="status-card status-card--processing"
        >
          <div class="processing-animation">
            <div class="processing-animation__ring"></div>
            <div class="processing-animation__icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polygon points="12,2 2,7 12,12 22,7 12,2" />
                <polyline points="2,17 12,22 22,17" />
                <polyline points="2,12 12,17 22,12" />
              </svg>
            </div>
          </div>
          <h4>{{ t("generating") }}</h4>
          <p>{{ t("cards_generated") }}: {{ cardCount }}</p>
          <div class="progress-bar">
            <div
              class="progress-bar__fill"
              :style="{ width: `${progressPercent}%` }"
            ></div>
          </div>
          <span class="progress-text">{{ progressPercent }}%</span>
        </div>

        <!-- Completed -->
        <div v-else-if="isCompleted" class="status-card status-card--completed">
          <div class="status-icon status-icon--success">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22,4 12,14.01 9,11.01" />
            </svg>
          </div>
          <h4>{{ t("flash_cards") }} {{ t("completed") }}!</h4>
          <p>{{ cardCount }} {{ t("cards_generated") }}</p>
          <button class="btn btn--primary btn--large" @click="startStudy">
            {{ t("start_study_session") }}
          </button>
        </div>

        <!-- Failed -->
        <div v-else-if="isFailed" class="status-card status-card--failed">
          <div class="status-icon status-icon--error">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h4>{{ t("generation_failed") }}</h4>
          <p>{{ errorMessage || t("submission_failed") }}</p>
          <button
            class="btn btn--primary"
            @click="handleRetry"
            :disabled="retrying"
          >
            {{ retrying ? t("loading") : t("retry") }}
          </button>
        </div>

        <!-- Timeout -->
        <div v-else-if="isTimeout" class="status-card status-card--timeout">
          <div class="status-icon status-icon--warning">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12,6 12,12 16,14" />
            </svg>
          </div>
          <h4>{{ t("taking_longer") }}</h4>
          <p>{{ t("wait_or_retry") }}</p>
          <button class="btn btn--secondary" @click="handleRetry">
            {{ t("retry") }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useMiaStore } from "../stores/miaStore";
import { usePolling } from "../services/pollingService";
import { OperationStatus } from "../types";
import { useI18n } from "../i18n";

export default {
  name: "FlashCardDetailsScreen",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const miaStore = useMiaStore();
    const polling = usePolling();
    const { t, isRtl } = useI18n();

    const deckId = ref(null);
    const deckTitle = ref("");
    const cardCount = ref(0);

    const loading = ref(true);
    const status = ref(null);
    const errorMessage = ref(null);
    const retrying = ref(false);
    const isTimeout = ref(false);
    const progressPercent = ref(0);

    const isProcessing = computed(
      () =>
        status.value === OperationStatus.PENDING ||
        status.value === OperationStatus.PROCESSING
    );
    const isCompleted = computed(
      () => status.value === OperationStatus.COMPLETED
    );
    const isFailed = computed(() => status.value === OperationStatus.FAILED);

    const fetchStatus = async () => {
      if (!deckId.value) return;

      const result = await miaStore.getDeckStatus(deckId.value);

      if (result.success) {
        const data = result.data;
        status.value = data.status;
        cardCount.value = data.card_count || data.cardCount || cardCount.value;
        errorMessage.value = data.error_message || data.errorMessage;

        if (data.progress !== undefined) {
          progressPercent.value = Math.round(data.progress * 100);
        } else if (isProcessing.value) {
          progressPercent.value = Math.min(progressPercent.value + 5, 90);
        }
      }
    };

    const startStatusPolling = () => {
      polling.start({
        id: `deck-${deckId.value}`,
        endpoint: `flash-cards/decks/${deckId.value}/status`,
        onUpdate: (data) => {
          status.value = data.status;
          cardCount.value =
            data.card_count || data.cardCount || cardCount.value;
          errorMessage.value = data.error_message || data.errorMessage;

          if (data.progress !== undefined) {
            progressPercent.value = Math.round(data.progress * 100);
          } else if (isProcessing.value) {
            progressPercent.value = Math.min(progressPercent.value + 5, 90);
          }
        },
        onComplete: (data) => {
          status.value = OperationStatus.COMPLETED;
          cardCount.value =
            data.card_count || data.cardCount || cardCount.value;
          progressPercent.value = 100;
        },
        onFailed: (data) => {
          status.value = OperationStatus.FAILED;
          errorMessage.value =
            data.error_message || data.errorMessage || "Generation failed";
        },
        onTimeout: () => {
          isTimeout.value = true;
        },
        checkComplete: (data) => data.status === OperationStatus.COMPLETED,
        checkFailed: (data) => data.status === OperationStatus.FAILED,
      });
    };

    const handleRetry = async () => {
      if (retrying.value) return;

      retrying.value = true;
      isTimeout.value = false;
      status.value = OperationStatus.PROCESSING;
      progressPercent.value = 0;

      const result = await miaStore.retryDeck(deckId.value);

      if (result.success) {
        startStatusPolling();
      } else {
        status.value = OperationStatus.FAILED;
        errorMessage.value = result.error || "Retry failed";
      }

      retrying.value = false;
    };

    const startStudy = () => {
      router.push({
        path: "/mia/flash-cards/study",
        query: { deckId: deckId.value },
      });
    };

    const goBack = () => {
      router.push("/mia/flash-cards");
    };

    onMounted(async () => {
      deckId.value = route.query.deckId;
      deckTitle.value = route.query.deckTitle || "";

      if (!deckId.value) {
        router.push("/mia/flash-cards");
        return;
      }

      await fetchStatus();
      loading.value = false;

      if (isProcessing.value) {
        startStatusPolling();
      }
    });

    onUnmounted(() => {
      polling.cleanup();
    });

    return {
      t,
      isRtl,
      deckId,
      deckTitle,
      cardCount,
      loading,
      status,
      errorMessage,
      retrying,
      isTimeout,
      progressPercent,
      isProcessing,
      isCompleted,
      isFailed,
      handleRetry,
      startStudy,
      goBack,
    };
  },
};
</script>

<style scoped>
.details-screen {
  min-height: 100vh;
  background: #f9fafb;
}

.screen-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 100;
}

.screen-header__back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: #374151;
  transition: background 0.2s ease;
}

.screen-header__back:hover {
  background: #f3f4f6;
}

.screen-header__title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.details-content {
  padding: 24px;
  max-width: 600px;
  margin: 0 auto;
}

.details-section {
  margin-bottom: 24px;
}

.status-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  padding: 48px 32px;
  text-align: center;
}

.status-card h4 {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 16px 0 8px;
}

.status-card p {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px;
}

.status-card--loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #6b7280;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.processing-animation {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

.processing-animation__ring {
  position: absolute;
  inset: 0;
  border: 4px solid #e5e7eb;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
}

.processing-animation__icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
}

.progress-bar {
  width: 100%;
  max-width: 300px;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  margin: 0 auto 12px;
  overflow: hidden;
}

.progress-bar__fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 14px;
  font-weight: 600;
  color: #6366f1;
}

.status-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.status-icon--success {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.status-icon--error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.status-icon--warning {
  background: rgba(249, 115, 22, 0.1);
  color: #f97316;
}

.btn {
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn--large {
  padding: 16px 32px;
  font-size: 16px;
  border-radius: 12px;
}

.btn--primary {
  background: #6366f1;
  color: white;
  border: none;
}

.btn--primary:hover:not(:disabled) {
  background: #4f46e5;
}

.btn--primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn--secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn--secondary:hover {
  background: #f3f4f6;
}

/* ========== DARK MODE SUPPORT ========== */
[data-bs-theme="dark"] .details-screen,
[data-theme="dark"] .details-screen,
.dark .details-screen {
  background: linear-gradient(135deg, #0f1419 0%, #1a2332 100%);
}

[data-bs-theme="dark"] .screen-header,
[data-theme="dark"] .screen-header,
.dark .screen-header {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .screen-header__back,
[data-theme="dark"] .screen-header__back,
.dark .screen-header__back {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .screen-header__back:hover,
[data-theme="dark"] .screen-header__back:hover,
.dark .screen-header__back:hover {
  background: rgba(0, 196, 68, 0.1);
}

[data-bs-theme="dark"] .screen-header__title,
[data-theme="dark"] .screen-header__title,
.dark .screen-header__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .screen-header__btn,
[data-theme="dark"] .screen-header__btn,
.dark .screen-header__btn {
  background: #334155;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .screen-header__btn:hover,
[data-theme="dark"] .screen-header__btn:hover,
.dark .screen-header__btn:hover {
  background: rgba(0, 196, 68, 0.15);
  color: #00c444;
}

[data-bs-theme="dark"] .status-card,
[data-theme="dark"] .status-card,
.dark .status-card {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .status-card h4,
[data-theme="dark"] .status-card h4,
.dark .status-card h4 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .status-card p,
[data-theme="dark"] .status-card p,
.dark .status-card p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .status-card--loading,
[data-theme="dark"] .status-card--loading,
.dark .status-card--loading {
  color: #94a3b8;
}

[data-bs-theme="dark"] .spinner,
[data-theme="dark"] .spinner,
.dark .spinner {
  border-color: #475569;
  border-top-color: #00c444;
}

[data-bs-theme="dark"] .processing-animation__ring,
[data-theme="dark"] .processing-animation__ring,
.dark .processing-animation__ring {
  border-color: #475569;
  border-top-color: #00c444;
}

[data-bs-theme="dark"] .processing-animation__icon,
[data-theme="dark"] .processing-animation__icon,
.dark .processing-animation__icon {
  color: #00c444;
}

[data-bs-theme="dark"] .progress-bar,
[data-theme="dark"] .progress-bar,
.dark .progress-bar {
  background: #475569;
}

[data-bs-theme="dark"] .progress-bar__fill,
[data-theme="dark"] .progress-bar__fill,
.dark .progress-bar__fill {
  background: linear-gradient(90deg, #00c444, #00a83b);
}

[data-bs-theme="dark"] .progress-text,
[data-theme="dark"] .progress-text,
.dark .progress-text {
  color: #00c444;
}

[data-bs-theme="dark"] .flash-cards-container,
[data-theme="dark"] .flash-cards-container,
.dark .flash-cards-container {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .flash-card,
[data-theme="dark"] .flash-card,
.dark .flash-card {
  background: #0f172a;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .flash-card__front,
[data-theme="dark"] .flash-card__back,
[data-theme="dark"] .flash-card__front,
[data-theme="dark"] .flash-card__back,
.dark .flash-card__front,
.dark .flash-card__back {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .btn--primary,
[data-theme="dark"] .btn--primary,
.dark .btn--primary {
  background: #00c444;
}

[data-bs-theme="dark"] .btn--primary:hover:not(:disabled),
[data-theme="dark"] .btn--primary:hover:not(:disabled),
.dark .btn--primary:hover:not(:disabled) {
  background: #00a83b;
}

[data-bs-theme="dark"] .btn--secondary,
[data-theme="dark"] .btn--secondary,
.dark .btn--secondary {
  background: #334155;
  color: #e2e8f0;
  border-color: #475569;
}

[data-bs-theme="dark"] .btn--secondary:hover,
[data-theme="dark"] .btn--secondary:hover,
.dark .btn--secondary:hover {
  background: #475569;
}
</style>
